import { useEffect, useImperativeHandle, forwardRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { CompanyIdentitySection } from "@/components/features/fiscal-profile/CompanyIdentitySection";
import { FiscalAutomationsSection } from "@/components/features/fiscal-profile/FiscalAutomationsSection";
import { FiscalSummarySection } from "@/components/features/fiscal-profile/FiscalSummarySection";
import { SpecialRegimesSection } from "@/components/features/fiscal-profile/SpecialRegimesSection";
import { TaxConfigurationSection } from "@/components/features/fiscal-profile/TaxConfigurationSection";
import { TaxObligationsSection } from "@/components/features/fiscal-profile/TaxObligationsSection";
import {
  validateFiscalProfileEditor,
} from "@/lib/fiscalProfileEditorMappers";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Who's filling in the form? Drives the second-person vs third-person copy
 * in the section headers ("Datos de tu empresa" vs "Datos de la empresa
 * del cliente"). Owner is the default.
 */
export type FiscalProfileEditorAudience = "owner" | "accountant";

export interface FiscalProfileEditorProps {
  idPrefix?: string;
  initialValues: FiscalProfileEditorValues;
  /** When this string changes, form resets to initialValues. */
  resetToken: string;
  hideCompanyType?: boolean;
  hideAgendaFields?: boolean;
  industryCode?: string | null;
  industryForLegends: string | null;
  showSubmitButton?: boolean;
  isSaving?: boolean;
  submitLabel?: string;
  onSubmit?: () => void;
  /** Called when validation result may change (e.g. onboarding Next button). */
  onValidationChange?: (valid: boolean) => void;
  /** Defaults to 'owner'. Pass 'accountant' from the accountant-onboarding flow. */
  audience?: FiscalProfileEditorAudience;
}

export interface FiscalProfileEditorRef {
  /** Returns true if validation passes (same rules as submit enable). */
  validate: () => boolean;
  /** Current form values (for imperative save from parent). */
  getValues: () => FiscalProfileEditorValues;
}

export const FiscalProfileEditor = forwardRef<
  FiscalProfileEditorRef,
  FiscalProfileEditorProps
>(function FiscalProfileEditorInner(
  {
    idPrefix = "fiscal",
    initialValues,
    resetToken,
    hideCompanyType = false,
    hideAgendaFields = false,
    industryCode,
    industryForLegends,
    showSubmitButton = true,
    isSaving = false,
    submitLabel,
    onSubmit,
    onValidationChange,
    audience = "owner",
  },
  ref
): JSX.Element {
  const { t } = useTranslation();
  const methods = useForm<FiscalProfileEditorValues>({
    defaultValues: initialValues,
    mode: "onChange",
  });

  const { reset, getValues, handleSubmit, control } = methods;

  const watchedValues = useWatch({ control }) as FiscalProfileEditorValues;

  useEffect(() => {
    reset(initialValues);
  }, [resetToken, reset, initialValues]);

  useEffect(() => {
    if (!onValidationChange) return;
    onValidationChange(
      validateFiscalProfileEditor(watchedValues, { hideAgendaFields })
    );
  }, [watchedValues, onValidationChange, hideAgendaFields]);

  const values = watchedValues;
  const canSave = validateFiscalProfileEditor(values, { hideAgendaFields });
  const defaultSubmitLabel = t("settings.fiscalProfile.save");

  useImperativeHandle(
    ref,
    () => ({
      validate: () => validateFiscalProfileEditor(getValues(), { hideAgendaFields }),
      getValues,
    }),
    [getValues, hideAgendaFields]
  );

  return (
    <FormProvider {...methods}>
      <div className="space-y-4">
        <CompanyIdentitySection
          idPrefix={idPrefix}
          hideCompanyType={hideCompanyType}
          industryCode={industryCode}
          audience={audience}
        />

        {!hideAgendaFields && (
          <TaxObligationsSection idPrefix={idPrefix} audience={audience} />
        )}

        <TaxConfigurationSection
          idPrefix={idPrefix}
          hideAgendaFields={hideAgendaFields}
          audience={audience}
        />

        <SpecialRegimesSection idPrefix={idPrefix} industry={industryForLegends} />

        {/* Same gate as TaxObligationsSection: hideAgendaFields means the free
            plan, which has no fiscal agenda, so there is no BPS obligation for
            the auto-request cron to act on. NOT gated on plan tier otherwise —
            see FiscalAutomationsSection's own docblock. */}
        {!hideAgendaFields && (
          <FiscalAutomationsSection idPrefix={idPrefix} audience={audience} />
        )}

        <FiscalSummarySection hideAgendaFields={hideAgendaFields} />

        {showSubmitButton && onSubmit && (
          <div className="flex justify-end border-t pt-4">
            <Button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSaving || !canSave}
              className="transition-colors duration-150"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("settings.fiscalProfile.saving")}
                </>
              ) : (
                submitLabel ?? defaultSubmitLabel
              )}
            </Button>
          </div>
        )}
      </div>
    </FormProvider>
  );
});

FiscalProfileEditor.displayName = "FiscalProfileEditor";
